import React from "react";
import Spinner from "@atlaskit/spinner";
import { PeopleCard, PeopleList, Pagination as PaginationUI } from "@caelor/ui";
import {
  Centered,
  PaginationWrapper,
  PeopleCardContainer,
  PeopleListContainer,
  Link,
} from "./styled";
import { usePeopleStore } from "../../../stores/use-people-store";
import {
  NewPaginationTypesMapper,
  PaginationTypes,
} from "@Shared/Pagination-Types";
import { useTranslation } from "react-i18next";
import { useStyleguideSetup } from "../../../hooks/use-styleguide-setup";
import { useStyleguideColors } from "../../../hooks/use-styleguide-colors";
import { DEFAULT_ACCENT_COLOR } from "../defaults";
import { createDynamicProfileLink } from "../../../helpers/cosmos-profile-link";

const LinkWrapper = ({ url, mode, openInNewTab, children }) => {
  if (mode === "view") {
    return (
      <Link
        href={url}
        target={openInNewTab ? "_blank" : "_top"}
        rel="noreferrer"
      >
        {children}
      </Link>
    );
  }
  return children;
};

const Mapper = ({ data, children }) => data.map((user) => children(user));

const Variant = (props) => {
  const { variant, children, isLoadingNextPage } = props;

  if (isLoadingNextPage)
    return (
      <Centered>
        <Spinner size="xlarge" />
      </Centered>
    );

  if (variant === "card") {
    return (
      <PeopleCardContainer {...props}>
        {children(PeopleCard, PeopleCardContainer)}
      </PeopleCardContainer>
    );
  }
  return (
    <PeopleListContainer {...props}>
      {children(PeopleList, PeopleListContainer)}
    </PeopleListContainer>
  );
};

const Pagination = ({ data = {}, pagination, queryPagination, children }) => {
  const { t } = useTranslation();

  const { hasNextPage, hasPrevPage, onNext, onPrev, disabled, page } =
    queryPagination;

  const buildData = () => {
    if (pagination === PaginationTypes.INFINITE || disabled) {
      return Object.keys(data)?.flatMap((key) => data[key]) || [];
    }
    if (pagination === PaginationTypes.CAROUSEL) {
      return data[page] || [];
    }
  };

  if (disabled) return children(buildData());

  return (
    <PaginationWrapper>
      {children(buildData())}
      <PaginationUI
        type={NewPaginationTypesMapper[pagination]}
        labels={{
          next:
            pagination === PaginationTypes.INFINITE
              ? t("show-more")
              : t("next"),
          prev: t("prev"),
        }}
        hasNext={hasNextPage}
        hasPrev={hasPrevPage}
        onNext={() => onNext()}
        onPrev={() => onPrev()}
      />
    </PaginationWrapper>
  );
};

function People({ data, queryPagination, mode, isCosmosInstalled }) {
  const { config } = usePeopleStore((state) => ({
    config: state.data.config,
  }));

  const { styleguideSetup } = useStyleguideSetup();

  const styleguideColors = useStyleguideColors({
    styleguideSetup,
  });

  const {
    variant,
    columns,
    pagination,
    cardBorderRadius,
    accentColor,
    openInNewTab,
  } = config;

  return (
    <Pagination
      data={data}
      pagination={pagination}
      queryPagination={queryPagination}
    >
      {(paginatedData) => (
        <Variant
          variant={variant}
          columns={columns}
          isLoadingNextPage={queryPagination.loading && !paginatedData?.length}
        >
          {(PeopleComponent, PeopleComponentContainer) => (
            <Mapper
              data={paginatedData}
              queryPagination={queryPagination}
              SpinnerContainer={PeopleComponentContainer}
            >
              {(user) => {
                return (
                  <LinkWrapper
                    key={user.accountId}
                    url={createDynamicProfileLink(
                      user.accountId,
                      isCosmosInstalled
                    )}
                    mode={mode}
                    openInNewTab={openInNewTab ?? true}
                  >
                    <PeopleComponent
                      {...user}
                      key={user.accountId}
                      borderRadius={cardBorderRadius}
                      accentColor={
                        accentColor ||
                        styleguideColors?.accent ||
                        DEFAULT_ACCENT_COLOR
                      }
                      subtitle={user.title}
                      label={user.footer}
                    />
                  </LinkWrapper>
                );
              }}
            </Mapper>
          )}
        </Variant>
      )}
    </Pagination>
  );
}

export default People;
